/**
 * Human-readable copy for MSAL errors surfaced to the user.
 *
 * MSAL v5 stopped shipping error prose in the bundle. Where v4 gave us
 * "Interaction is currently in progress. Please ensure that this interaction has
 * been completed before calling an interactive API.", v5's BrowserAuthError
 * message is generated as:
 *
 *   `See https://aka.ms/msal.js.errors#${code} for details`
 *
 * So rendering `error.message` — which is what the login page used to do — now
 * shows the user a documentation URL. The error *codes* are stable and remain
 * the right thing to branch on, so this maps the ones a user can actually
 * encounter to copy that tells them what to do next.
 *
 * Server errors are the exception: `ServerError` still carries the identity
 * platform's own `errorMessage`, so AADSTS text is real prose and worth
 * matching on.
 */

// Entra's "redirect URI does not match" error. Arrives as server text rather
// than a browser error code, so it is matched separately from CODE_MESSAGES.
const AADSTS_REDIRECT_URI_MISMATCH = 'AADSTS50011';

const GENERIC_MESSAGE = 'Authentication failed. Please try again.';

const CODE_MESSAGES = {
  // Interaction lock. src/utils/msalTokenManager.js recovers from a stale lock
  // automatically, so reaching the user means recovery already failed once.
  interaction_in_progress:
    'Sign-in is already in progress. Reload the page and try again.',

  // Popup blocked or closed. Both are recoverable by the user.
  popup_window_error:
    'Your browser blocked the sign-in popup. Allow popups for this site and try again.',
  empty_window_error:
    'Your browser blocked the sign-in popup. Allow popups for this site and try again.',
  user_cancelled: 'Sign-in was cancelled.',

  // Redirect-bridge failures, new in v5. These mean the response could not get
  // back from /redirect.html to the app — a misconfigured bridge (caching,
  // COOP, or same-origin framing blocked) looks exactly like this, so the copy
  // points at a retry while staying honest that it may not be the user's doing.
  timed_out:
    'Sign-in timed out before it completed. Please try again — if it keeps happening, contact IT.',
  redirect_bridge_empty_response:
    'Sign-in did not complete. Please try again — if it keeps happening, contact IT.',

  // Network. Worth distinguishing so the user does not go hunting for an
  // account problem.
  no_network_connectivity:
    'Could not reach Microsoft sign-in. Check your connection and try again.',
  post_request_failed:
    'Could not reach Microsoft sign-in. Check your connection and try again.',
  get_request_failed:
    'Could not reach Microsoft sign-in. Check your connection and try again.',
};

/**
 * True when the error is Entra rejecting our redirect URI. Kept separate
 * because the fix is an admin action, not something the user can retry, and
 * because the message names the URI that has to be registered.
 *
 * @param {unknown} error
 * @returns {boolean}
 */
export function isRedirectUriMismatch(error) {
  if (!error) return false;
  return error.errorCode === 'redirect_uri_mismatch'
    || (typeof error.message === 'string' && error.message.includes(AADSTS_REDIRECT_URI_MISMATCH));
}

/**
 * Copy to show for an MSAL error.
 *
 * Never returns a raw MSAL message: under v5 that is a documentation URL, and
 * under v4 it was internal library prose. Both are noise to the person reading
 * the login page.
 *
 * @param {unknown} error - an MSAL error, or anything falsy
 * @param {string} [redirectUri] - the URI we asked Entra to redirect to, named
 *   in the mismatch case so an admin knows exactly what to register
 * @returns {string|null} the message, or null when there is no error
 */
export function msalErrorMessage(error, redirectUri) {
  if (!error) return null;

  if (isRedirectUriMismatch(error)) {
    const uri = redirectUri || 'the sign-in redirect URI';
    return `This URL is not registered as a redirect URI in Microsoft Entra ID. `
      + `An admin needs to add "${uri}" to the app registration, or configure the `
      + `environment variables in Netlify that enable automatic registration.`;
  }

  return CODE_MESSAGES[error.errorCode] || GENERIC_MESSAGE;
}
